<template>
  <BaseSideModal
    class="import-run-modal"
    :width="'960px'"
    :show-footer="true"
    @close="$emit('close')"
  >
    <template #title>{{ $t('paymentImportHistory.runTitle') }}</template>

    <div class="run-content">
      <p v-if="loading" class="body-text color--dark-purple">{{ $t('paymentImportHistory.loading') }}</p>
      <p v-else-if="loadError" class="body-text color--red">{{ loadError }}</p>

      <template v-else-if="run">
        <section class="run-summary mb_24">
          <p class="body-text--bold mb_8">{{ run.fileName || '—' }}</p>
          <p class="body-text color--dark-purple">
            {{ formatDateTime(run.created) }} ·
            {{ $t('paymentImportHistory.appliedOf', {applied: run.appliedCount, rows: run.rowCount}) }} ·
            <template v-if="run.followingCount">
              {{ $t('excelUploader.paymentsSummary.followingCanceled', {count: run.followingCount}) }} ·
            </template>
            {{ $t(`paymentImportHistory.status.${run.status}`) }}
          </p>
          <!-- Per currency and never summed into one figure: pesos plus dollars through today's rate
               is not a fact about these policies. -->
          <p v-if="run.debtByCurrency && run.debtByCurrency.length" class="body-text mt_8">
            {{ $t('paymentImportHistory.debt') }}
            <span v-for="(d, i) in run.debtByCurrency" :key="d.currencyCode">
              <span v-if="i">&nbsp;·&nbsp;</span>{{ formatMoney(d.unpaidTotal) }} {{ d.currencyCode }}
            </span>
          </p>
        </section>

        <!-- What undo can and cannot take back. Both sentences are true, which is the point: the
             warning people expect -- "a reminder went out" -- is not one of them. -->
        <section class="run-warning mb_24">
          <p class="body-text mb_8">{{ $t('paymentImportHistory.undoWarning.reminders') }}</p>
          <p class="body-text">{{ $t('paymentImportHistory.undoWarning.analytics') }}</p>
        </section>

        <div class="run-actions mb_16">
          <!-- The same list the summary screen links to. A run is most often opened to answer "what did
               this actually change", and the payments table answers that better than this one. -->
          <router-link
            class="body-text color--purple"
            :to="{ name: 'payments', query: { importRunId: runId } }"
          >
            {{ $t('paymentImportHistory.viewImportedPayments') }}
          </router-link>
          <BaseButton
            v-if="canUndoAnything"
            :disabled="undoing"
            @click="undo(null)"
          >
            {{ $t('paymentImportHistory.undoAll', {count: undoablePolicies.length}) }}
          </BaseButton>
          <span v-else class="body-text color--dark-purple">
            {{ $t('paymentImportHistory.nothingToUndo') }}
          </span>
        </div>

        <p v-if="undoError" class="body-text color--red mb_8">{{ undoError }}</p>

        <div v-for="policy in run.policies" :key="policy.insurancePolicyId" class="run-policy">
          <header class="run-policy__header">
            <a
              v-if="policyHrefFor(policy)"
              :href="policyHrefFor(policy)"
              target="_blank"
              class="body-text body-text--bold body-text--link color--purple"
            >{{ policy.policyNumber || policy.insurancePolicyId }}</a>
            <span v-else class="body-text--bold">
              {{ policy.policyNumber || policy.insurancePolicyId }}
            </span>
            <span class="body-text color--dark-purple">
              {{ $t('paymentImportHistory.rowsOnPolicy', {count: policy.rowCount}) }}
            </span>
            <span
              v-if="!policy.undoneAt"
              class="body-text color--purple cursor__pointer"
              @click="undo(policy.insurancePolicyId)"
            >
              {{ $t('paymentImportHistory.undoPolicy', {count: policy.rowCount}) }}
            </span>
            <span v-else class="body-text color--dark-purple">
              {{ $t('paymentImportHistory.restoredOn', {date: formatDateTime(policy.undoneAt)}) }}
            </span>
          </header>
          <p v-if="policy.undoRefusedReason" class="body-text color--red">
            {{ policy.undoRefusedReason }}
          </p>

          <PaymentDiffTable :pairs="pairsFor(policy)"/>
        </div>
      </template>
    </div>

    <template #footer>
      <BaseButton type="outline" @click="$emit('close')">
        {{ $t('paymentImportHistory.close') }}
      </BaseButton>
      <BaseButton type="outline" @click="exportCsv" :disabled="!run">
        {{ $t('paymentImportHistory.exportCsv') }}
      </BaseButton>
    </template>
  </BaseSideModal>
</template>

<script>
import BaseSideModal from '@/views/RenovaDashboard/components/ui-kit/BaseSideModal/BaseSideModal';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import {getBrowserMoneyLocale} from '@/utils/amountFormatter';
import PaymentDiffTable from './PaymentDiffTable';

export default {
  name: 'PaymentImportRunModal',
  components: {BaseSideModal, BaseButton, PaymentDiffTable},
  props: {
    runId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      run: null,
      loading: true,
      loadError: '',
      undoing: false,
      undoError: '',
    };
  },
  computed: {
    undoablePolicies() {
      return (this.run?.policies || []).filter((p) => !p.undoneAt);
    },
    canUndoAnything() {
      return this.undoablePolicies.length > 0;
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.loading = true;
      this.loadError = '';
      try {
        this.run = await this.$store.dispatch('fetchPaymentImportRunDetail', this.runId);
      } catch (e) {
        this.loadError = this.$t('paymentImportHistory.loadFailed');
      } finally {
        this.loading = false;
      }
    },
    // Pairs the two snapshots by payment id, which is what makes this table readable: the same receipt
    // on one line before and after, rather than two lists to compare by eye.
    pairsFor(policy) {
      const after = new Map((policy.after || []).map((r) => [r.paymentId, r]));
      const before = new Map((policy.before || []).map((r) => [r.paymentId, r]));
      return Array.from(new Set([...before.keys(), ...after.keys()])).map((paymentId) => {
        const b = before.get(paymentId) || null;
        const a = after.get(paymentId) || null;
        return {
          key: paymentId, dueDate: (b || a)?.dueDate, before: b, after: a
        };
      });
    },
    policyHrefFor(policy) {
      return policy?.insurancePolicyId ? `/admin/policies/${policy.insurancePolicyId}` : null;
    },
    async undo(insurancePolicyId) {
      if (this.undoing) return;
      this.undoing = true;
      this.undoError = '';
      try {
        const result = await this.$store.dispatch('undoPaymentImportRun', {
          runId: this.runId,
          insurancePolicyId,
        });
        if (result?.refusedCount) {
          this.undoError = (result.rows || [])
            .filter((r) => !r.restored)
            .map((r) => `${r.policyNumber || r.insurancePolicyId}: ${r.reason}`)
            .join('; ');
        }
        await this.fetch();
        this.$emit('undone', result);
      } catch (e) {
        this.undoError = this.$t('paymentImportHistory.undoFailed');
      } finally {
        this.undoing = false;
      }
    },
    // Built here rather than asked of the backend: the table on screen is already the whole run, so a
    // second endpoint would be a second chance for the two to disagree.
    exportCsv() {
      if (!this.run) return;
      // payment_id first, and it is the reason this export matters: without it a client cannot
      // line our result up against their own system row by row, which is the one thing that was
      // blocking them from reconciling this themselves. The id is already on every pair -- the run
      // snapshots store payment ids deliberately -- it was simply never written out.
      const header = [
        'payment_id', 'policy_number', 'due_date', 'amount_before', 'status_before',
        'amount_after', 'status_after', 'collected_amount', 'collected_date',
      ];
      const lines = [header.join(',')];
      (this.run.policies || []).forEach((policy) => {
        this.pairsFor(policy).forEach((pair) => {
          lines.push([
            pair.paymentId,
            policy.policyNumber || policy.insurancePolicyId,
            this.formatDate(pair.dueDate),
            pair.before?.amount ?? '',
            pair.before?.status ?? '',
            pair.after?.amount ?? '',
            pair.after?.status ?? '',
            pair.after?.paidAmount ?? '',
            this.formatDate(pair.after?.paidDate),
          ].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
        });
      });
      const blob = new Blob([lines.join('\n')], {type: 'text/csv;charset=utf-8;'});
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payment_import_${this.runId.slice(0, 8)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    formatMoney(val) {
      if (val === null || val === undefined || val === '') return '—';
      const num = Number(val);
      if (Number.isNaN(num)) return String(val);
      return new Intl.NumberFormat(getBrowserMoneyLocale(), {minimumFractionDigits: 2, maximumFractionDigits: 2})
        .format(num);
    },
    formatDate(val) {
      return val ? String(val).slice(0, 10) : '—';
    },
    formatDateTime(val) {
      return val ? String(val).slice(0, 16).replace('T', ' ') : '—';
    },
  },
};
</script>

<style lang="scss">
.run-content {
  padding-right: 8px;
}

.run-summary {
  border-left: 3px solid var(--solid-lavender-grey);
  padding-left: 12px;
}

.run-warning {
  background: #FFF8E1;
  border-left: 4px solid #F59E0B;
  padding: 12px 16px;
  border-radius: 4px;
}

.run-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.run-policy {
  margin-top: 24px;
}

.run-policy__header {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
</style>
